import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  MenuItem,
  Box,
  FormControl,
  InputLabel,
  Select,
  DialogContentText,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Visibility,
  Edit,
  Delete,
  Close,
  Search,
} from '@mui/icons-material';
import httpClient from '../../api/axios';
import { useSelector } from 'react-redux';

// Update the httpClient baseURL directly
// httpClient.defaults.baseURL = 'http://192.168.0.206:4000/';

const OrderMaster = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const userId = useSelector((state) => state.auth.userId);

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const [open, setOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderToDelete, setOrderToDelete] = useState(null);
  const [formStatus, setFormStatus] = useState('Edit Order');
  const [formData, setFormData] = useState({
    userId: '',
    items: [
      {
        productId: '',
        quantityKg: '',
        pricePerKg: '',
        totalPrice: 0,
      },
    ],
    totalAmount: 0,
    deliveryAddress: '',
    contactNumber: '',
    paymentStatus: '',
    orderStatus: '',
  });

  const paymentStatusOptions = [
    'Pending',
    'Completed',
    'Failed',
    'Refunded',
    'Partially Paid',
    'Paid',
  ];

  const orderStatusOptions = [
    'Pending',
    'Processing',
    'Shipped',
    'Delivered',
    'Cancelled',
    'Returned',
    'Placed',
    'Confirmed',
  ];

  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // Fetch all orders on component mount
  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await httpClient.get('/api/admin/orders', {
        headers: {
          'user-id': userId,
        },
      });

      setOrders(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError('Failed to fetch orders. Please try again.');
      setSnackbar({
        open: true,
        message: 'Failed to fetch orders',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleViewClose = () => {
    setViewOpen(false);
  };

  const handleDeleteClose = () => {
    setDeleteOpen(false);
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleChange = (e, index = null) => {
    const { name, value } = e.target;
    if (index !== null) {
      const updatedItems = [...formData.items];
      updatedItems[index][name] = value;

      const qty = parseFloat(updatedItems[index].quantityKg || 0);
      const price = parseFloat(updatedItems[index].pricePerKg || 0);
      updatedItems[index].totalPrice = qty * price;

      const totalAmount = updatedItems.reduce(
        (acc, curr) => acc + parseFloat(curr.totalPrice || 0),
        0
      );
      setFormData({ ...formData, items: updatedItems, totalAmount });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleAddClick = () => {
    setFormData({
      ...formData,
      items: [
        ...formData.items,
        {
          productId: '',
          quantityKg: '',
          pricePerKg: '',
          totalPrice: 0,
        },
      ],
    });
  };

  const handleRemoveClick = (index) => {
    const updatedItems = [...formData.items];
    updatedItems.splice(index, 1);
    const totalAmount = updatedItems.reduce(
      (acc, curr) => acc + parseFloat(curr.totalPrice || 0),
      0
    );
    setFormData({ ...formData, items: updatedItems, totalAmount });
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const updatedOrder = { ...formData };

      // For edit operation
      if (formData._id) {
        await httpClient.put(`/api/orders`, updatedOrder);
        setSnackbar({
          open: true,
          message: 'Order updated successfully',
          severity: 'success',
        });
      } else {
        // For add operation - not in your current scope
        await httpClient.post('/api/orders', updatedOrder);
        setSnackbar({
          open: true,
          message: 'Order added successfully',
          severity: 'success',
        });
      }

      fetchOrders(); // Refresh the list
      setOpen(false);
    } catch (err) {
      console.error('Error saving order:', err);
      setSnackbar({
        open: true,
        message: 'Failed to save order',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleViewClick = (order) => {
    setSelectedOrder(order);
    setViewOpen(true);
  };

  const handleEditClick = (order) => {
    setFormStatus('Edit Order');
    // Format the data to match the form structure
    const formattedOrder = {
      ...order,
      orderId: order?._id,
      userId: order?.userId._id || order.userId,
      items: order?.items.map((item) => ({
        productId: item?._id,
        productName: item?.productId?.name || '',
        quantityKg: item?.quantityKg,
        pricePerKg: item?.pricePerKg,
        totalPrice: item?.totalPrice,
      })),
    };
    setFormData(formattedOrder);
    setOpen(true);
  };

  const handleDeleteClick = (order) => {
    setOrderToDelete(order);
    setDeleteOpen(true);
  };

  const confirmDelete = async () => {
    setLoading(true);
    try {
      await httpClient.delete(
        `/api/admin/orders/${orderToDelete._id}`,
        {
          headers: {
            'user-id': userId,
          },
        }
      );
      fetchOrders(); // Refresh the list
      setSnackbar({
        open: true,
        message: 'Order deleted successfully',
        severity: 'success',
      });
      setDeleteOpen(false);
    } catch (err) {
      console.error('Error deleting order:', err);
      setSnackbar({
        open: true,
        message: 'Failed to delete order',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setPage(0); // Reset to first page when searching
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString();
  };

  // Filter orders based on search query
  const filteredOrders = orders.filter((order) => {
    // Handle cases where userId might be an object or string
    const userIdValue =
      typeof order.userId === 'object'
        ? order.userId?._id || order.userId?.phone || ''
        : order.userId || '';

    return (
      String(userIdValue)
        .toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
      order.deliveryAddress
        ?.toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
      order.contactNumber?.includes(searchQuery)
    );
  });

  // Responsive column display based on screen size
  const getVisibleColumns = () => {
    if (isMobile) {
      return [
        'userId',
        'items',
        'totalAmount',
        'orderStatus',
        'actions',
      ];
    } else if (isTablet) {
      return [
        'userId',
        'items',
        'totalAmount',
        'paymentStatus',
        'orderStatus',
        'actions',
      ];
    }
    return [
      'userId',
      'items',
      'totalAmount',
      'address',
      'contact',
      'paymentStatus',
      'orderStatus',
      'actions',
    ];
  };

  const visibleColumns = getVisibleColumns();

  // Helper function to display userId correctly
  const displayUserId = (userId) => {
    if (!userId) return 'N/A';
    if (typeof userId === 'object') {
      return userId.phone || userId._id || 'N/A';
    }
    return userId;
  };

  const UpdateOrderAndPaymentStatus = async () => {
    try {
      setLoading(true);
      // setOpen(true);
      let obj = {
        orderId: formData?.orderId,
        paymentStatus: formData?.paymentStatus,
        orderStatus: formData?.orderStatus,
      };
      const res = await httpClient.put(
        `/api/admin/order-and-payment/status`,
        obj,
        {
          headers: {
            'user-id': userId,
          },
        }
      );

      setSnackbar({
        open: true,
        message: 'Successful to save order',
        severity: 'success',
      });

      fetchOrders();
      console.log(res.data);
    } catch (err) {
      console.error('Error saving order:', err);
      setSnackbar({
        open: true,
        message: 'Failed to save order',
        severity: 'error',
      });
    } finally {
      setLoading(false);
      setOpen(false);
    }
  };

  return (
    <div className='flex justify-center items-start min-h-screen bg-gray-100 p-4 w-full'>
      <Paper className='w-full max-w-7xl p-3 md:p-6 shadow-lg'>
        <Typography
          variant='h5'
          className='text-center font-bold mb-4'
        >
          Order Master
        </Typography>

        {/* Search Box */}
        <Box display='flex' gap={2} mb={3}>
          <TextField
            label='Search Orders'
            variant='outlined'
            fullWidth
            value={searchQuery}
            onChange={handleSearchChange}
            InputProps={{
              endAdornment: (
                <IconButton>
                  <Search />
                </IconButton>
              ),
            }}
          />
        </Box>

        {loading && (
          <Box display='flex' justifyContent='center' my={3}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity='error' sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TableContainer>
          <Table size={isMobile ? 'small' : 'medium'}>
            <TableHead>
              <TableRow>
                {visibleColumns.includes('userId') && (
                  <TableCell>
                    <strong>User</strong>
                  </TableCell>
                )}
                {visibleColumns.includes('items') && (
                  <TableCell>
                    <strong>Items</strong>
                  </TableCell>
                )}
                {visibleColumns.includes('totalAmount') && (
                  <TableCell>
                    <strong>Total Amount</strong>
                  </TableCell>
                )}
                {visibleColumns.includes('address') && (
                  <TableCell>
                    <strong>Delivery Address</strong>
                  </TableCell>
                )}
                {visibleColumns.includes('contact') && (
                  <TableCell>
                    <strong>Contact Number</strong>
                  </TableCell>
                )}
                {visibleColumns.includes('paymentStatus') && (
                  <TableCell>
                    <strong>Payment Status</strong>
                  </TableCell>
                )}
                {visibleColumns.includes('orderStatus') && (
                  <TableCell>
                    <strong>Order Status</strong>
                  </TableCell>
                )}
                {visibleColumns.includes('actions') && (
                  <TableCell>
                    <strong>Actions</strong>
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredOrders.length > 0 ? (
                filteredOrders
                  .slice(
                    page * rowsPerPage,
                    page * rowsPerPage + rowsPerPage
                  )
                  .map((order) => (
                    <TableRow key={order._id}>
                      {visibleColumns.includes('userId') && (
                        <TableCell>
                          {displayUserId(order.userId)}
                        </TableCell>
                      )}
                      {visibleColumns.includes('items') && (
                        <TableCell>
                          <Table
                            size='small'
                            className='border-collapse border-none'
                          >
                            <TableHead>
                              <TableRow>
                                <TableCell
                                  padding='none'
                                  style={{ border: 'none' }}
                                >
                                  <strong>Product</strong>
                                </TableCell>
                                <TableCell
                                  padding='none'
                                  style={{ border: 'none' }}
                                >
                                  <strong>Qty (kg)</strong>
                                </TableCell>
                                <TableCell
                                  padding='none'
                                  style={{ border: 'none' }}
                                >
                                  <strong>Price/kg</strong>
                                </TableCell>
                                <TableCell
                                  padding='none'
                                  style={{ border: 'none' }}
                                >
                                  <strong>Total</strong>
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {order.items.map((item, idx) => {
                                const productName =
                                  item.productId &&
                                  item.productId.name
                                    ? item.productId.name
                                    : typeof item.productId ===
                                      'string'
                                    ? item.productId
                                    : 'N/A';

                                return (
                                  <TableRow key={idx}>
                                    <TableCell
                                      padding='none'
                                      style={{ border: 'none' }}
                                    >
                                      {productName}
                                    </TableCell>
                                    <TableCell
                                      padding='none'
                                      style={{ border: 'none' }}
                                    >
                                      {item.quantityKg}
                                    </TableCell>
                                    <TableCell
                                      padding='none'
                                      style={{ border: 'none' }}
                                    >
                                      ₹{item.pricePerKg}
                                    </TableCell>
                                    <TableCell
                                      padding='none'
                                      style={{ border: 'none' }}
                                    >
                                      ₹{item.totalPrice}
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableCell>
                      )}
                      {visibleColumns.includes('totalAmount') && (
                        <TableCell>₹{order.totalAmount}</TableCell>
                      )}
                      {visibleColumns.includes('address') && (
                        <TableCell>{order.deliveryAddress}</TableCell>
                      )}
                      {visibleColumns.includes('contact') && (
                        <TableCell>{order.contactNumber}</TableCell>
                      )}
                      {visibleColumns.includes('paymentStatus') && (
                        <TableCell>{order.paymentStatus}</TableCell>
                      )}
                      {visibleColumns.includes('orderStatus') && (
                        <TableCell>{order.orderStatus}</TableCell>
                      )}
                      {visibleColumns.includes('actions') && (
                        <TableCell>
                          <Box
                            display='flex'
                            flexDirection={
                              isMobile ? 'column' : 'row'
                            }
                            gap={1}
                          >
                            <IconButton
                              onClick={() => handleViewClick(order)}
                              className='!bg-amber-500'
                              size='small'
                              sx={{ mb: isMobile ? 1 : 0 }}
                            >
                              <Visibility />
                            </IconButton>
                            <IconButton
                              onClick={() => handleEditClick(order)}
                              className='!bg-amber-500'
                              size='small'
                              sx={{ mb: isMobile ? 1 : 0 }}
                            >
                              <Edit />
                            </IconButton>
                            <IconButton
                              onClick={() => handleDeleteClick(order)}
                              className='!bg-amber-500'
                              size='small'
                            >
                              <Delete />
                            </IconButton>
                          </Box>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={visibleColumns.length}
                    align='center'
                  >
                    {loading
                      ? 'Loading orders...'
                      : 'No Orders Found'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component='div'
            count={filteredOrders.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </TableContainer>
      </Paper>

      {/* Edit Order Modal */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth='md'
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          {formStatus}
          <IconButton
            edge='end'
            onClick={handleClose}
            style={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            label='User ID'
            fullWidth
            margin='normal'
            name='userId'
            value={formData.userId || ''}
            onChange={handleChange}
            disabled
          />
          {formData.items.map((item, index) => (
            <Box
              key={index}
              display='flex'
              flexDirection={isMobile ? 'column' : 'row'}
              gap={2}
              mb={2}
              alignItems={isMobile ? 'stretch' : 'center'}
            >
              <TextField
                label='Product ID'
                name='productId'
                fullWidth={isMobile}
                value={item.productId}
                onChange={(e) => handleChange(e, index)}
                disabled
              />
              <TextField
                label='Quantity (kg)'
                name='quantityKg'
                type='number'
                fullWidth={isMobile}
                value={item.quantityKg}
                onChange={(e) => handleChange(e, index)}
                disabled
              />
              <TextField
                label='Price/kg'
                name='pricePerKg'
                type='number'
                fullWidth={isMobile}
                value={item.pricePerKg}
                onChange={(e) => handleChange(e, index)}
                disabled
              />
              <TextField
                label='Total Price'
                name='totalPrice'
                type='number'
                fullWidth={isMobile}
                value={item.totalPrice}
                InputProps={{ readOnly: true }}
                disabled
              />
              {formData.items.length > 1 && (
                <IconButton
                  onClick={() => handleRemoveClick(index)}
                  color='error'
                  aria-label='remove item'
                >
                  <Delete />
                </IconButton>
              )}
            </Box>
          ))}
          {/* <Button
            onClick={handleAddClick}
            className='!bg-amber-500 !text-white'
            startIcon={<Edit />}
          >
            Add Item
          </Button> */}
          <TextField
            label='Total Amount'
            fullWidth
            margin='normal'
            name='totalAmount'
            type='number'
            value={formData.totalAmount}
            InputProps={{ readOnly: true }}
            disabled
          />
          <TextField
            label='Delivery Address'
            fullWidth
            margin='normal'
            name='deliveryAddress'
            value={formData.deliveryAddress}
            onChange={handleChange}
            multiline
            rows={3}
            disabled
          />
          <TextField
            label='Contact Number'
            fullWidth
            margin='normal'
            name='contactNumber'
            value={formData.contactNumber}
            onChange={handleChange}
            disabled
          />
          {/* Payment Status Dropdown */}
          <FormControl fullWidth margin='normal'>
            <InputLabel>Payment Status</InputLabel>
            <Select
              name='paymentStatus'
              value={formData?.paymentStatus}
              label='Payment Status'
              onChange={(e) =>
                handleSelectChange('paymentStatus', e.target.value)
              }
            >
              {paymentStatusOptions?.map((status) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Order Status Dropdown */}
          <FormControl fullWidth margin='normal'>
            <InputLabel>Order Status</InputLabel>
            <Select
              name='orderStatus'
              value={formData.orderStatus}
              label='Order Status'
              onChange={(e) =>
                handleSelectChange('orderStatus', e.target.value)
              }
            >
              {orderStatusOptions?.map((status) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={UpdateOrderAndPaymentStatus}
            variant='contained'
            className='!bg-amber-500 !text-white'
            style={{ width: isMobile ? '100%' : '200px' }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : formStatus}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Order Modal */}
      <Dialog
        open={viewOpen}
        onClose={handleViewClose}
        maxWidth='md'
        fullScreen={isMobile}
      >
        <DialogTitle>
          View Order
          <IconButton
            edge='end'
            onClick={handleViewClose}
            style={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedOrder && (
            <div className='space-y-2'>
              <Typography variant='h6' gutterBottom>
                Order ID: {selectedOrder?._id}
              </Typography>
              <Typography variant='subtitle1' gutterBottom>
                User Name: {selectedOrder?.userId?.name}
                {/* {typeof selectedOrder.userId === 'object'
                  ? selectedOrder.userId.phone ||
                    selectedOrder.userId._id
                  : selectedOrder.userId || 'N/A'} */}
              </Typography>
              <Typography variant='subtitle1' gutterBottom>
                Created At: {formatDate(selectedOrder.createdAt)}
              </Typography>

              <Typography variant='h6' sx={{ mt: 2 }} gutterBottom>
                Items:
              </Typography>
              <TableContainer component={Paper}>
                <Table size='small'>
                  <TableHead>
                    <TableRow>
                      <TableCell>Product</TableCell>
                      <TableCell>Quantity (kg)</TableCell>
                      <TableCell>Price/kg</TableCell>
                      <TableCell>Total Price</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedOrder.items.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          {item.productId && item.productId.name
                            ? item.productId.name
                            : typeof item.productId === 'string'
                            ? item.productId
                            : 'N/A'}
                        </TableCell>
                        <TableCell>{item.quantityKg}</TableCell>
                        <TableCell>₹{item.pricePerKg}</TableCell>
                        <TableCell>₹{item.totalPrice}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Typography variant='h6' sx={{ mt: 2 }} gutterBottom>
                Order Summary:
              </Typography>
              <Typography variant='body1'>
                Total Amount: ₹{selectedOrder.totalAmount}
              </Typography>
              <Typography variant='body1'>
                Delivery Address: {selectedOrder.deliveryAddress}
              </Typography>
              <Typography variant='body1'>
                Contact Number: {selectedOrder.contactNumber}
              </Typography>
              <Typography variant='body1'>
                Payment Status: {selectedOrder.paymentStatus}
              </Typography>
              <Typography variant='body1'>
                Order Status: {selectedOrder.orderStatus}
              </Typography>
              {selectedOrder.paymentMethod && (
                <Typography variant='body1'>
                  Payment Method: {selectedOrder.paymentMethod}
                </Typography>
              )}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleViewClose}
            className='!bg-amber-500 !text-white'
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog
        open={deleteOpen}
        onClose={handleDeleteClose}
        fullWidth
        maxWidth='sm'
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this order? This action
            cannot be undone.
          </DialogContentText>
          {orderToDelete && (
            <Box mt={2}>
              <Typography variant='subtitle1'>
                Order ID: {orderToDelete._id}
              </Typography>
              <Typography variant='body1'>
                User:{' '}
                {typeof orderToDelete.userId === 'object'
                  ? orderToDelete.userId.phone ||
                    orderToDelete.userId._id
                  : orderToDelete.userId || 'N/A'}
              </Typography>
              <Typography variant='body1'>
                Total Amount: ₹{orderToDelete.totalAmount}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteClose} color='secondary'>
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            color='primary'
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default OrderMaster;
